import React, { useState, useEffect, useContext } from "react";
import { withRouter } from "react-router-dom";
import { withOktaAuth } from "@okta/okta-react";
import {
  withStyles,
  Grid,
  Dialog,
  Typography,
  IconButton,
  RadioGroup,
  Radio,
  InputLabel,
  FormControlLabel,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { createCheckRequest } from "../../../../API/Check";
import { getListValues } from "../../../../API/List";
import { SearchRecords } from "../../..";
import withUserDetails from "../../../../HOC/withUserDetails";
import { recordTypes } from "../../../../API/config";
import { NoAccessScreen } from "../../../../Component";
import SearchableSelect from "../../../../Component/Forms/SearchableSelect";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../Component/CustomButton";

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const CreateCheck = (props) => {
  const { classes, user, hasPermission } = props;

  const { t } = useTranslation();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [checkType, setCheckType] = useState("KYC");
  const [selectedCountry, setSelectedCountry] = useState();
  const confidentialityOptions = [
    { value: true, label: t("labels.common.yes") },
    { value: false, label: t("labels.common.no") },
  ];
  const [confidentiality, setConfidentiality] = useState({
    value: false,
    label: t("labels.common.no"),
  });

  const [entityId, setEntityId] = useState();
  const [mdmId, setMdmId] = useState();
  const [dnbId, setDnbId] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [countryErrorMessage, setCountryErrorMessage] = useState();
  const [checkTypeErrorMessage, setCheckTypeErrorMessage] = useState();
  const [canCreateKyc, setCanCreateKyc] = useState();
  const [canCreateAml, setCanCreateAml] = useState();
  const [kycAllowed, setKycAllowed] = useState();
  const [amlAllowed, setAmlAllowed] = useState();
  const { showErrorMessage, showConfirmation } = useContext(ModalDialogContext);

  useEffect(() => {
    const evalPermissions = async () => {
      const canCreateKyc = await hasPermission(
        recordTypes.checkRequest.name,
        recordTypes.checkRequest.actions.createKycCheck.name,
      );
      const canCreateAml = await hasPermission(
        recordTypes.checkRequest.name,
        recordTypes.checkRequest.actions.createAmlCheck.name,
      );
      setCanCreateKyc(canCreateKyc);
      setCanCreateAml(canCreateAml);
    };

    const loadCountries = async () => {
      setIsLoading(true);
      try {
        const resuts = await getListValues({ listType: "CL" });
        const { data = [] } = resuts;
        setCountryList(
          data.map((c) => {
            return { value: c.code, label: c.value };
          }),
        );
        const userCountry = data.find((c) => c.isoCode2 === user.locale);
        if (userCountry) {
          setSelectedCountry({
            value: userCountry.code,
            label: userCountry.value,
          });
        }
      } catch (error) {
        showErrorMessage({ error });
      }
      setIsLoading(false);
    };
    evalPermissions();
    if (openCreateDialog) {
      loadCountries();
      setConfidentiality({
        value: false,
        label: t("labels.common.no"),
      });
    }
  }, [openCreateDialog]);

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          // height: "70vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const checkTypeChanged = (e) => {
    setCheckType(e.target.value);
  };

  const handleConfidentialityChange = (e) => {
    setConfidentiality(e);
  };

  const renderCreateForm = () => {
    if (!kycAllowed && !amlAllowed)
      return (
        <span className={classes.errorText}>
          {t("messages.check.initiation.checkReqAlreadyExists")} !{" "}
        </span>
      );
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3} style={{ paddingLeft: 15 }}>
          <InputLabel className={classes.inputLabel}>
            {t("labels.check.createForm.checkType")}
          </InputLabel>

          <RadioGroup
            size="small"
            margin="dense"
            aria-label="checkType"
            name="checkType"
            value={checkType}
            onChange={checkTypeChanged}
            row
          >
            {kycAllowed && (
              <FormControlLabel
                value="KYC"
                control={<Radio color="primary" />}
                label={t("labels.checks.KYC")}
                labelPlacement="end"
              />
            )}
            {amlAllowed && (
              <FormControlLabel
                value="AML"
                control={<Radio color="primary" />}
                label={t("labels.checks.amlKyc")}
                labelPlacement="end"
              />
            )}
          </RadioGroup>
          <div className={classes.errorText}>{checkTypeErrorMessage}</div>
        </Grid>
        <Grid item sm={6} xs={12} md={5}>
          <SearchableSelect
            label={t("labels.check.createForm.jllCountry")}
            options={countryList}
            value={selectedCountry}
            changeHandler={setSelectedCountry}
          />
          <div className={classes.errorText}>{countryErrorMessage}</div>
        </Grid>
        <Grid item xs={12} md={3} sm={6}>
          <SearchableSelect
            label={t("labels.check.createForm.isConfidential")}
            options={confidentialityOptions}
            value={confidentiality}
            changeHandler={handleConfidentialityChange}
          />
        </Grid>
      </Grid>
    );
  };

  const viewRecord = (record, source) => {
    const { id } = record;
    if (source === "Local") {
      props.history.push("/viewCheck?id=" + id);
    }
  };

  const onRecordSelect = (record, source) => {
    const { id, checkRequests = [], scores = [] } = record || {};
    const kycCr = checkRequests.find((cr) => cr.check_type === "KYC");
    const amlCr = checkRequests.find((cr) => cr.check_type === "AML");
    const kycScore = scores.find((cr) => cr.check_type === "KYC");
    const amlScore = scores.find((cr) => cr.check_type === "AML");
    const kycAllowed =
      canCreateKyc && (!kycCr || kycCr.cancelled || kycScore.expired);
    const amlAllowed =
      canCreateAml && (!amlCr || amlCr.cancelled || amlScore.expired);
    setKycAllowed(kycAllowed);
    setAmlAllowed(amlAllowed);

    if (source === "Local") {
      setEntityId(id);
    } else {
      setEntityId(null);
    }
    if (source === "MDM") {
      setMdmId(id);
    } else {
      setMdmId(null);
    }
    if (source === "DNB") {
      setDnbId(record.dunsNumber);
    } else {
      setDnbId(null);
    }
    setOpenCreateDialog(true);
  };

  const closeCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const onPressGoToYourCheck = (createdId) => {
    const url = createdId ? `/viewCheck?id=${createdId}` : `/`;
    props.history.push(url);
  };

  const submitCheck = async () => {
    if (!selectedCountry || !selectedCountry.value) {
      setCountryErrorMessage(t("messages.check.initiation.countryError"));
      return;
    }
    if (!checkType) {
      setCheckTypeErrorMessage(t("messages.check.initiation.checkTypeError"));
      return;
    }
    setCountryErrorMessage();
    setIsLoading(true);
    setIsCreating(true);
    try {
      const result = await createCheckRequest({
        checkType,
        entityId,
        mdmId,
        dnbId,
        confidential: confidentiality.value,
        countryId: selectedCountry.value,
      });
      const { data: createdId } = result;
      showConfirmation({
        title: t("labels.common.dialog.success"),
        yesLabel: t("messages.check.initiation.goToYourCheck"),
        noLabel: t("messages.check.initiation.createAnotherCheck"),
        message: t("messages.check.initiation.checkCreated"),
        onPressYes: () => onPressGoToYourCheck(createdId),
      });
    } catch (error) {
      showErrorMessage({ error });
    }
    setIsLoading(false);
    setIsCreating(false);
    setOpenCreateDialog(false);
  };

  const searchRecordActions = [
    {
      iconClass: "far fa-arrow-circle-right",
      position: "row",
      tooltip: t("labels.record.search.table.actions.viewRecord"),
      sources: ["Local"],
      callBack: viewRecord,
    },
    {
      iconClass: "far fa-plus",
      position: "row",
      tooltip: t("labels.record.search.table.actions.initiateCheck"),
      callBack: onRecordSelect,
    },
  ];

  if (!canCreateKyc && !canCreateAml) {
    return <NoAccessScreen />;
  }

  return (
    <>
      <SearchRecords
        recordActions={searchRecordActions}
        user={user}
        hasPermission={hasPermission}
        hideCheckTypeSelection={false}
        disableCheckTypeSelection={false}
        defaultCheckType={"KYC"}
      />
      <Dialog
        disableBackdropClick
        onClose={closeCreateDialog}
        aria-labelledby="customized-dialog-title"
        open={openCreateDialog}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle id="customized-dialog-title" onClose={closeCreateDialog}>
          <Typography className={classes.panelTitle}>
            <span>
              {t("labels.check.createForm.header")} {isLoading}
            </span>
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? renderLoadingIndicator() : renderCreateForm()}
        </DialogContent>
        <DialogActions>
          
          <CustomButton 
            variant="secondary"
            isDisabled={isLoading}
            onClick={closeCreateDialog}
          >
            {t("labels.common.cancel")}
          </CustomButton>


          {(kycAllowed || amlAllowed) && (
            <CustomButton 
              variant="primary"
              loadingText={
                isCreating ? t("labels.check.createForm.creating") : ""
              }
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={submitCheck}
            >
              {t("labels.common.submit")}
            </CustomButton>


          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withUserDetails(
  withRouter(
    withOktaAuth(withStyles(styles, { withTheme: true })(CreateCheck)),
  ),
);
